import {
  ProductVariantAvailabilityStatus,
  showPrice,
  getLocaleWithInternationalFallback,
  SwarovskiOptikSegmentShort,
} from "@swo/shared";
import { identity } from "fp-ts/lib/function";
import { PdpData } from "../types";
import { DealerLocatorSvgIcon } from "../../icons";
import { getVariantCartState } from "./get-variant-cart-state";
import { PDPSwarovskiProductVariantFrontend, PDPSwarovskiProductFrontend } from "../state/types";
import { dealerLocatorTrackingAspect } from "../../../../domain/google-tag-manager/dealer-locator-tracking-aspect";
import { ReactComponent as MailSvg } from "../../../../assets/icons/mail.svg";
import { SwarovskiOptikLocaleFrontend, type TranslatedValue } from "@swo/storefront";
import { usePDPAvailabilityNotificationState } from "../use-pdp-availability-notification-state";
import { ProductDetailPageDataShared } from "@swo/storefront";
import { PDPImagesGridImageT } from "../../PDP_Gallery/types";
import { PdpAvailabilityState, PDPButtonWithAction, pdpDisabledActionOf } from "./types";
import type { PDPAvailabilityNotificationState } from "./notification/state";

export type FormatDateF = (date: Date, options?: Intl.DateTimeFormatOptions) => string;

export function usePDPAvailabilityBarState(
  selectedVariant: PDPSwarovskiProductVariantFrontend,
  availability: ProductVariantAvailabilityStatus,
  primaryImage: PDPImagesGridImageT | undefined,
  addToCart: () => Promise<void>,
  locale: SwarovskiOptikLocaleFrontend,
  segment: SwarovskiOptikSegmentShort,
  translations: PdpData["translations"]["pdp"],
  links: ProductDetailPageDataShared["links"],
): PdpAvailabilityState {
  const { pdpAvailabilityNotificationState, AvailabilityNotification } = usePDPAvailabilityNotificationState(
    locale,
    segment,
    translations,
    primaryImage,
    selectedVariant,
    availability,
    links.privacyPolicyLink,
  );

  const onDealerLocatorButtonClick = () => {
    dealerLocatorTrackingAspect.openDealerLocator(selectedVariant.sku, availability.tag).push();
  };

  return computePDPAvailabilityBarState(
    pdpAvailabilityNotificationState,
    availability,
    addToCart,
    locale,
    translations,
    links,
    onDealerLocatorButtonClick,
    AvailabilityNotification,
  );
}

export function computePDPAvailabilityBarState(
  state: PDPAvailabilityNotificationState,
  availability: ProductVariantAvailabilityStatus,
  addToCart: () => Promise<void>,
  locale: SwarovskiOptikLocaleFrontend,
  translations: PdpData["translations"]["pdp"],
  links: ProductDetailPageDataShared["links"],
  onDealerLocatorButtonClick: (() => void) | undefined,
  AvailabilityNotification: React.ReactNode,
) {
  const activeDealerLocatorButton: PDPButtonWithAction = {
    tag: "internal-link",
    href: links.dealerLocatorLink,
    label: translations["product_detail_page.dealerLocatorLabel"],
    icon: DealerLocatorSvgIcon,
    onClick: onDealerLocatorButtonClick,
  };

  function getAvailabilityNotificationButton(state: PDPAvailabilityNotificationState): PDPButtonWithAction {
    if (state.tag === "impossible") {
      return pdpDisabledActionOf(translations["availabilityNotificationBtnLabel"], MailSvg);
    }

    if (state.tag === "closed") {
      return {
        tag: "action",
        icon: MailSvg,
        label: translations["availabilityNotificationBtnLabel"],
        f: state.openSignUpScreen,
      };
    }

    return pdpDisabledActionOf(translations["availabilityNotificationBtnLabel"], MailSvg);
  }

  const availabilityNotificationButton = getAvailabilityNotificationButton(state);

  const availabilityNotification = (text: TranslatedValue) =>
    identity<PdpAvailabilityState>({
      action: availabilityNotificationButton,
      extension: {
        tag: "component",
        Component: AvailabilityNotification,
        text,
      },
    });

  switch (locale.shopMode.pdpMainAction) {
    case "cart": {
      const formatDate: FormatDateF = (date, options) => {
        const localeWithInternationalFallback = getLocaleWithInternationalFallback(locale);

        return new Date(date).toLocaleDateString(
          localeWithInternationalFallback,
          options || {
            month: "long",
            year: "numeric",
          },
        );
      };

      return getVariantCartState(
        availability,
        formatDate,
        addToCart,
        translations,
        activeDealerLocatorButton,
        availabilityNotification,
      );
    }

    case "dealer-locator": {
      return identity<PdpAvailabilityState>({
        action: activeDealerLocatorButton,
        extension: null,
      });
    }

    case "availability-subscription": {
      return availabilityNotification(translations["primaryActionAvailabilityNotificationExplanation"]);
    }

    default: {
      const _: never = locale.shopMode.pdpMainAction;

      return _;
    }
  }
}
